/**
 * Colonne de jeu centrée et échelle sur grand écran (phase 2, v0.35.0, A78).
 *
 * DiceOrDie reste un jeu **portrait** : sur PC et tablette il se joue dans une
 * colonne centrée, sans mise en page élargie. Deux choses se calculent à partir
 * de la taille de la fenêtre (`columnLayout`, fonction pure) :
 *
 *  - la **largeur de la colonne** (en px de conception) :
 *    `min(480, hauteur × 9 / 16)`, plancher 400 px, bornée par la fenêtre ;
 *    sur téléphone (fenêtre < 480 px) elle vaut toute la largeur, comme avant ;
 *  - l'**échelle** de l'interface : 1 sur téléphone et dès que la fenêtre fait
 *    moins de 844 px de haut (hauteur d'un téléphone de référence), puis
 *    `hauteur / 844` jusqu'à ×1,5 sur un grand écran ; jamais plus que
 *    `largeur / 480` pour que la colonne tienne dans la fenêtre.
 *
 * L'échelle est appliquée par `transform: scale()` sur `#app` (voir
 * `src/style.css`) : la mise en page garde ses px de conception, le texte et le
 * DOM sont rastérisés à la taille réelle. Le canevas 3D, lui, est un bitmap :
 * `battleView3d.ts` lit `getUiScale()` pour le rendre à la taille réelle
 * (tampon = taille CSS × `devicePixelRatio` × échelle) et ramener les
 * coordonnées du pointeur à l'échelle de conception. Les fenêtres et toasts
 * sont montés dans `#app` (`overlayRoot()`) pour suivre la colonne.
 *
 * Ne dépend pas du mode « autonome » de la PWA : seule la taille de la fenêtre
 * compte, et un téléphone (< 480 px de large) reste à l'échelle 1.
 */

/** Largeur maximale de la colonne (px de conception). */
export const COLUMN_MAX_W = 480;
/** Plancher de la largeur de colonne (fenêtre basse, téléphone en paysage). */
export const COLUMN_MIN_W = 400;
/** Rapport largeur / hauteur de la colonne. */
export const COLUMN_ASPECT = 9 / 16;
/** Hauteur de référence (téléphone de 844 px) : en dessous, échelle 1. */
export const REFERENCE_H = 844;
/** Agrandissement maximal de l'interface. */
export const SCALE_MAX = 1.5;

export interface ColumnLayout {
  /** Facteur d'agrandissement de l'interface (≥ 1). */
  scale: number;
  /** Largeur de la colonne, px de conception (entier). */
  width: number;
  /** Hauteur de la colonne, px de conception (= fenêtre ÷ échelle). */
  height: number;
}

/** Calcule colonne et échelle pour une fenêtre `vw × vh` (px CSS). */
export function columnLayout(vw: number, vh: number): ColumnLayout {
  const w = Number.isFinite(vw) && vw > 0 ? vw : COLUMN_MAX_W;
  const h = Number.isFinite(vh) && vh > 0 ? vh : REFERENCE_H;
  let scale = 1;
  if (w >= COLUMN_MAX_W) {
    scale = Math.min(SCALE_MAX, Math.max(1, Math.min(h / REFERENCE_H, w / COLUMN_MAX_W)));
    scale = Math.round(scale * 1000) / 1000;
  }
  const height = h / scale;
  const wanted = Math.min(COLUMN_MAX_W, Math.max(COLUMN_MIN_W, height * COLUMN_ASPECT));
  const width = Math.floor(Math.min(wanted, w / scale));
  return { scale, width, height };
}

// ─────────────────────────────────────────────────────────────── État & DOM

let currentScale = 1;
const listeners = new Set<(scale: number) => void>();

/** Échelle actuelle de l'interface (1 tant que `applyColumnScale` n'a pas tourné). */
export function getUiScale(): number {
  return currentScale;
}

/** Abonnement aux changements d'échelle (la vue 3D reconstruit son tampon). */
export function onUiScaleChange(fn: (scale: number) => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/**
 * Pose `--ui-scale` et `--col-w` sur `<html>` (lus par `src/style.css`) et les

 * tient à jour à chaque redimensionnement. `data-ui-scaled` n'est posé que si
 * l'échelle dépasse 1 : l'interface téléphone n'a ainsi aucune transformation ;
 * `data-col-framed` quand la fenêtre est plus large que la colonne.
 */
export function applyColumnScale(win: Window = window): () => void {
  const root = win.document.documentElement;
  const update = (): void => {
    // `innerWidth` / `innerHeight` : la fenêtre de mise en page (le
    // visualViewport bouge avec le pincement et le clavier virtuel).
    const layout = columnLayout(win.innerWidth, win.innerHeight);
    root.style.setProperty('--ui-scale', String(layout.scale));
    root.style.setProperty('--col-w', `${layout.width}px`);
    if (layout.scale > 1) root.setAttribute('data-ui-scaled', '');
    else root.removeAttribute('data-ui-scaled');
    // Colonne plus étroite que la fenêtre : le fond décoratif et ses liserés se voient.
    const framed = win.innerWidth > layout.width * layout.scale + 1;
    if (framed) root.setAttribute('data-col-framed', '');
    else root.removeAttribute('data-col-framed');
    if (layout.scale !== currentScale) {
      currentScale = layout.scale;
      for (const fn of [...listeners]) fn(currentScale);
    }
  };
  update();
  win.addEventListener('resize', update);
  win.addEventListener('orientationchange', update);
  return () => {
    win.removeEventListener('resize', update);
    win.removeEventListener('orientationchange', update);
  };
}

/**
 * Parent des fenêtres et toasts : `#app`, pour qu'ils restent dans la colonne
 * et suivent son échelle. Repli : `document.body`.
 */
export function overlayRoot(): HTMLElement {
  return document.getElementById('app') ?? document.body;
}
